import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const FEEDBACK_26_CO_STEPS = [
  'overall',
  'quality',
  'improvement',
  'sponsors',
  'future',
  'extras',
  'aura',
  'thank-you',
] as const;

export type Feedback26CoStep = (typeof FEEDBACK_26_CO_STEPS)[number];

// The fifteen quality dimensions the 26-co form asks about. The server owns the labels
// (FeedbackEditions, app/lib/feedback_editions.rb) and hands them to the page as a prop;
// only the keys live here, because the store is keyed by them.
export type EventQualityKey26Co =
  | 'oficina'
  | 'wifi'
  | 'comida'
  | 'software'
  | 'comunicacion'
  | 'branding'
  | 'mentores'
  | 'jueces'
  | 'sponsors'
  | 'nivelTecnico'
  | 'tracks'
  | 'premios'
  | 'procesoEvaluacion'
  | 'organizacion'
  | 'platanusHackMarket';

export type ParticipationIntent = 'yes' | 'no' | 'maybe';
export type SponsorWorkIntent = 'yes' | 'no' | 'already_did';
export type StartupIntent = 'yes' | 'no' | 'already_building';
export type FundingPreference = 'bootstrapped' | 'vc' | 'other';
export type StartupAmbition =
  | 'up_to_100k'
  | '100k_to_1m'
  | '1m_to_10m'
  | '10m_plus'
  | 'not_sure';
export type FeedbackUsagePermission = 'yes_with_name' | 'yes_anonymous' | 'no';

type Ratings26Co = Record<EventQualityKey26Co, number>;

const INITIAL_QUALITY_RATINGS: Ratings26Co = {
  oficina: 0,
  wifi: 0,
  comida: 0,
  software: 0,
  comunicacion: 0,
  branding: 0,
  mentores: 0,
  jueces: 0,
  sponsors: 0,
  nivelTecnico: 0,
  tracks: 0,
  premios: 0,
  procesoEvaluacion: 0,
  organizacion: 0,
  platanusHackMarket: 0,
};

export interface Feedback26CoFormState {
  overallRating?: number;
  npsScore?: number;
  participationIntent?: ParticipationIntent;
  eventQualityRatings: Ratings26Co;
  bestPart?: string;
  worstPart?: string;
  suggestions?: string;
  sponsorUnaidedRecall?: string;
  sponsorsInteracted?: string[];
  sponsorWorkIntent?: SponsorWorkIntent;
  sponsorComments?: string;
  startupIntent?: StartupIntent;
  fundingPreference?: FundingPreference;
  startupAmbition?: StartupAmbition;
  howHeardAbout?: string;
  additionalComments?: string;
  mediaUrls?: string[];
  feedbackUsagePermission?: FeedbackUsagePermission;
  mentorRating?: number;
  auraRating?: number;
  hackRacerRating?: number;
}

const INITIAL_FORM_STATE: Feedback26CoFormState = {
  overallRating: undefined,
  npsScore: undefined,
  participationIntent: undefined,
  eventQualityRatings: INITIAL_QUALITY_RATINGS,
  bestPart: '',
  worstPart: '',
  suggestions: '',
  sponsorUnaidedRecall: '',
  sponsorsInteracted: [],
  sponsorWorkIntent: undefined,
  sponsorComments: '',
  startupIntent: undefined,
  fundingPreference: undefined,
  startupAmbition: undefined,
  howHeardAbout: '',
  additionalComments: '',
  mediaUrls: [],
  feedbackUsagePermission: undefined,
  mentorRating: undefined,
  auraRating: undefined,
  hackRacerRating: undefined,
};

interface Feedback26CoStore {
  formData: Feedback26CoFormState;
  currentStep: Feedback26CoStep;
  maxStepReached: Feedback26CoStep;
  isLoading: boolean;
  error: string | null;
  isSubmitted: boolean;

  setOverallRating: (rating: number) => void;
  setNpsScore: (score: number) => void;
  setParticipationIntent: (intent: ParticipationIntent) => void;
  setEventQualityRating: (key: EventQualityKey26Co, value: number) => void;
  setBestPart: (text: string) => void;
  setWorstPart: (text: string) => void;
  setSuggestions: (text: string) => void;
  setSponsorUnaidedRecall: (text: string) => void;
  setSponsorsInteracted: (sponsors: string[]) => void;
  setSponsorWorkIntent: (intent: SponsorWorkIntent) => void;
  setSponsorComments: (text: string) => void;
  setStartupIntent: (intent: StartupIntent) => void;
  setFundingPreference: (preference: FundingPreference) => void;
  setStartupAmbition: (ambition: StartupAmbition) => void;
  setHowHeardAbout: (text: string) => void;
  setAdditionalComments: (text: string) => void;
  setMediaUrls: (urls: string[]) => void;
  setFeedbackUsagePermission: (permission: FeedbackUsagePermission) => void;
  setMentorRating: (rating: number) => void;
  setAuraRating: (rating: number) => void;
  setHackRacerRating: (rating: number) => void;

  goToStep: (step: Feedback26CoStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  canGoBack: () => boolean;

  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setIsSubmitted: (submitted: boolean) => void;
  resetForm: () => void;
  clearStorage: () => void;
}

const STORAGE_KEY = 'platanus-hack-feedback-26-co';

export const useFeedback26CoStore = create<Feedback26CoStore>()(
  persist(
    (set, get) => ({
      formData: INITIAL_FORM_STATE,
      currentStep: 'overall',
      maxStepReached: 'overall',
      isLoading: false,
      error: null,
      isSubmitted: false,

      setOverallRating: (rating) =>
        set((state) => ({
          formData: { ...state.formData, overallRating: rating },
        })),
      setNpsScore: (score) =>
        set((state) => ({
          formData: { ...state.formData, npsScore: score },
        })),
      setParticipationIntent: (intent) =>
        set((state) => ({
          formData: { ...state.formData, participationIntent: intent },
        })),
      setEventQualityRating: (key, value) =>
        set((state) => ({
          formData: {
            ...state.formData,
            eventQualityRatings: {
              ...state.formData.eventQualityRatings,
              [key]: value,
            },
          },
        })),
      setBestPart: (text) =>
        set((state) => ({
          formData: { ...state.formData, bestPart: text },
        })),
      setWorstPart: (text) =>
        set((state) => ({
          formData: { ...state.formData, worstPart: text },
        })),
      setSuggestions: (text) =>
        set((state) => ({
          formData: { ...state.formData, suggestions: text },
        })),
      setSponsorUnaidedRecall: (text) =>
        set((state) => ({
          formData: { ...state.formData, sponsorUnaidedRecall: text },
        })),
      setSponsorsInteracted: (sponsors) =>
        set((state) => ({
          formData: { ...state.formData, sponsorsInteracted: sponsors },
        })),
      setSponsorWorkIntent: (intent) =>
        set((state) => ({
          formData: { ...state.formData, sponsorWorkIntent: intent },
        })),
      setSponsorComments: (text) =>
        set((state) => ({
          formData: { ...state.formData, sponsorComments: text },
        })),
      setStartupIntent: (intent) =>
        set((state) => ({
          formData: { ...state.formData, startupIntent: intent },
        })),
      setFundingPreference: (preference) =>
        set((state) => ({
          formData: { ...state.formData, fundingPreference: preference },
        })),
      setStartupAmbition: (ambition) =>
        set((state) => ({
          formData: { ...state.formData, startupAmbition: ambition },
        })),
      setHowHeardAbout: (text) =>
        set((state) => ({
          formData: { ...state.formData, howHeardAbout: text },
        })),
      setAdditionalComments: (text) =>
        set((state) => ({
          formData: { ...state.formData, additionalComments: text },
        })),
      setMediaUrls: (urls) =>
        set((state) => ({
          formData: { ...state.formData, mediaUrls: urls },
        })),
      setFeedbackUsagePermission: (permission) =>
        set((state) => ({
          formData: { ...state.formData, feedbackUsagePermission: permission },
        })),
      setMentorRating: (rating) =>
        set((state) => ({
          formData: { ...state.formData, mentorRating: rating },
        })),
      setAuraRating: (rating) =>
        set((state) => ({
          formData: { ...state.formData, auraRating: rating },
        })),
      setHackRacerRating: (rating) =>
        set((state) => ({
          formData: { ...state.formData, hackRacerRating: rating },
        })),

      goToStep: (step) => {
        set({ currentStep: step });
      },
      nextStep: () => {
        set((state) => {
          const currentIndex = FEEDBACK_26_CO_STEPS.indexOf(state.currentStep);
          const nextStep = FEEDBACK_26_CO_STEPS[currentIndex + 1];
          if (nextStep) {
            const nextIndex = FEEDBACK_26_CO_STEPS.indexOf(nextStep);
            const maxIndex = FEEDBACK_26_CO_STEPS.indexOf(state.maxStepReached);
            const newMaxStepReached =
              nextIndex > maxIndex ? nextStep : state.maxStepReached;
            return { currentStep: nextStep, maxStepReached: newMaxStepReached };
          }
          return {};
        });
      },
      previousStep: () => {
        set((state) => {
          const currentIndex = FEEDBACK_26_CO_STEPS.indexOf(state.currentStep);
          const prevStep = FEEDBACK_26_CO_STEPS[currentIndex - 1];
          return prevStep ? { currentStep: prevStep } : {};
        });
      },
      canGoBack: () => {
        const { currentStep } = get();
        return currentStep !== 'overall';
      },

      setIsLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setIsSubmitted: (submitted) => set({ isSubmitted: submitted }),

      resetForm: () => {
        set({
          formData: INITIAL_FORM_STATE,
          currentStep: 'overall',
          maxStepReached: 'overall',
          error: null,
          isSubmitted: false,
        });
      },
      clearStorage: () => {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(STORAGE_KEY);
        }
      },
    }),
    {
      name: STORAGE_KEY,
      storage:
        typeof window !== 'undefined'
          ? {
              getItem: (key: string) => {
                const item = window.localStorage.getItem(key);
                return item ? JSON.parse(item) : null;
              },
              setItem: (key: string, value: unknown) => {
                window.localStorage.setItem(key, JSON.stringify(value));
              },
              removeItem: (key: string) => {
                window.localStorage.removeItem(key);
              },
            }
          : {
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
            },
    },
  ),
);
